import { PageHeading } from '@/components';
import type { SelectedNetwork } from '@/types';
import { usePayoutDatesV2Query } from '@/services/revenueApi';
import { BreadcrumbItem, Breadcrumbs, useDateFormatter } from '@acast-tech/decibel';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { Link } from 'react-router-dom';
import { handleMonthSelectTracking } from '@/utils/mixpanelUtils/trackingUtils';
import PayoutsDownloadTrigger from '@/components/drawers/DownloadButton/PayoutsDownloadTrigger';
import { DateString, YearString } from '@/types/state/filter';

const getMonthName = (date: Date) => Intl.DateTimeFormat('en', { month: 'long' }).format(date);
const getMonth2Digit = (date: Date) => Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);

const getPayoutDateFilterValues = (payoutDates?: string[]) => {
  if (!payoutDates) {
    return [];
  }
  return payoutDates?.map((payoutDateStr) => {
    const date = new Date(payoutDateStr);
    const payoutYear = date.getFullYear();
    const payoutMonth2Digit = getMonth2Digit(date);
    const payoutMonthName = getMonthName(date);

    return {
      value: `${payoutYear}/${payoutMonth2Digit}`,
      label: `${payoutMonthName} ${payoutYear}`,
    };
  });
};

export const CustomPageHeading = ({
  selectedNetwork,
  month,
  year,
}: {
  selectedNetwork: SelectedNetwork;
  month: string;
  year: string;
}) => {
  const ldClient = useLDClient();
  const isPayoutsDownloadEnabled = ldClient?.variation('payoutsDownload', false) ?? false;
  const monthFormatter = useDateFormatter({ month: 'long' });
  const { data: payoutsDataQuery } = usePayoutDatesV2Query(selectedNetwork.id);
  const payoutDates = payoutsDataQuery?.payout_dates;

  const monthName = getMonthName(new Date(parseInt(year), parseInt(month) - 1));
  const payoutName = `${monthName} ${year}`;

  // Create a stable selectedItem that doesn't become undefined during navigation
  const selectedItem = { value: `${year}/${month}`, label: payoutName };

  return (
    <>
      <div className="my-6 mb-0 py-4">
        <Breadcrumbs separator="/">
          <BreadcrumbItem>
            <Link to={`/${selectedNetwork.id}/payouts/${year}`}>{selectedNetwork.name}</Link>
          </BreadcrumbItem>
          <BreadcrumbItem disabled>
            <span className="font-medium text-black">{payoutName}</span>
          </BreadcrumbItem>
        </Breadcrumbs>
      </div>
      <div className="flex items-center justify-between gap-4">
        <PageHeading
          fallbackText="No Payout Selected"
          getPath={(value: string) => `/${selectedNetwork.id}/payouts/${value}`}
          items={getPayoutDateFilterValues(payoutDates)}
          selectedItem={selectedItem}
          onSelect={(payoutValue: string) => {
            // payoutValue is in format "YYYY/MM", convert to "YYYY-MM-DD" format
            const [payoutYear, payoutMonth] = payoutValue.split('/');
            const payoutDateString = `${payoutYear}-${payoutMonth}-01` as DateString;
            handleMonthSelectTracking(payoutDateString, payoutYear as YearString, monthFormatter);
          }}
        />
        {isPayoutsDownloadEnabled && (
          <PayoutsDownloadTrigger year={year} month={month} />
        )}
      </div>
    </>
  );
};
